import { useEffect, useRef } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Float, Environment, Lightformer, Sparkles } from "@react-three/drei";
import * as THREE from "three";
import { heroProgress, heroMouse } from "@/lib/scrollState";

const ChromeKnot = () => {
    const mesh = useRef(null);

    useFrame((state, delta) => {
        if (!mesh.current) return;
        const p = heroProgress.value;
        mesh.current.rotation.y += delta * 0.18;
        mesh.current.rotation.x = THREE.MathUtils.damp(
            mesh.current.rotation.x,
            heroMouse.y * 0.35 + p * 0.6,
            2.5,
            delta,
        );
        mesh.current.rotation.z = THREE.MathUtils.damp(
            mesh.current.rotation.z,
            heroMouse.x * 0.25 - p * 0.4,
            2.5,
            delta,
        );
    });

    return (
        <Float speed={1.4} rotationIntensity={0.35} floatIntensity={1.1}>
            <mesh ref={mesh}>
                <torusKnotGeometry args={[1.15, 0.36, 260, 48]} />
                <meshStandardMaterial
                    color="#e4e4e7"
                    metalness={1}
                    roughness={0.12}
                    envMapIntensity={1.6}
                />
            </mesh>
        </Float>
    );
};

const HaloRing = () => {
    const ring = useRef(null);
    useFrame((state, delta) => {
        if (!ring.current) return;
        ring.current.rotation.z += delta * 0.05;
        ring.current.rotation.x = -0.9 + heroMouse.y * 0.15;
    });
    return (
        <mesh ref={ring} position={[0, 0, -3.2]} scale={2.9}>
            <torusGeometry args={[1.5, 0.006, 8, 160]} />
            <meshBasicMaterial color="#3f3f46" transparent opacity={0.6} />
        </mesh>
    );
};

// Camera dolly + group parallax driven by scroll and pointer for cinematic depth.
const Rig = ({ children }) => {
    const group = useRef(null);
    const { camera } = useThree();
    useFrame((state, delta) => {
        const p = heroProgress.value;
        camera.position.z = THREE.MathUtils.damp(camera.position.z, 5.4 + p * 2.6, 3, delta);
        camera.position.y = THREE.MathUtils.damp(camera.position.y, p * 0.9, 3, delta);
        camera.lookAt(0, 0, 0);
        if (group.current) {
            group.current.position.x = THREE.MathUtils.damp(
                group.current.position.x,
                heroMouse.x * 0.6,
                3,
                delta,
            );
            group.current.position.y = THREE.MathUtils.damp(
                group.current.position.y,
                heroMouse.y * 0.4 - p * 0.5,
                3,
                delta,
            );
        }
    });
    return <group ref={group}>{children}</group>;
};

export const HeroScene = () => {
    useEffect(() => {
        const onMove = (e) => {
            heroMouse.x = (e.clientX / window.innerWidth) * 2 - 1;
            heroMouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
        };
        window.addEventListener("mousemove", onMove);
        return () => window.removeEventListener("mousemove", onMove);
    }, []);

    return (
        <Canvas
            dpr={[1, 2]}
            camera={{ position: [0, 0, 5.4], fov: 42 }}
            gl={{ antialias: true, alpha: true }}
            className="!pointer-events-none"
        >
            <fog attach="fog" args={["#050505", 6.5, 13]} />
            <ambientLight intensity={0.2} />
            <Rig>
                <HaloRing />
                <ChromeKnot />
                <Sparkles
                    count={90}
                    scale={11}
                    size={1.6}
                    speed={0.25}
                    color="#ffffff"
                    opacity={0.35}
                />
            </Rig>
            <Environment resolution={256}>
                <group rotation={[-Math.PI / 3, 0, 0]}>
                    <Lightformer form="circle" intensity={5} position={[0, 5, -9]} scale={2.4} />
                    <Lightformer form="rect" intensity={2.5} position={[-5, 1, -1]} scale={[3.5, 2]} />
                    <Lightformer
                        form="rect"
                        intensity={1.6}
                        color="#dbe4ff"
                        position={[5, -1, -1]}
                        scale={[3.5, 2]}
                    />
                    <Lightformer form="rect" intensity={1} position={[0, -5, 2]} scale={[4, 2]} />
                </group>
            </Environment>
        </Canvas>
    );
};
